/* =========================================================================
   App state, rendering, and the view-level actions.
   ========================================================================= */

import { recipes, byId, saveRecipe, removeRecipe, saveAll, lastSaveError } from './data/store.js'
import { releaseBlobUrl } from './data/images.js'
import { registerActions, registerInputActions, toast, closeModal, $ } from './ui/dom.js'
import { listHTML, detailHTML } from './ui/views.js'
import { pickImage, openImageEditor } from './ui/imageEditor.js'

export const state = {
  view: 'list',
  currentId: null,
  q: '',
  cat: 'All',
  servesOverride: {},
  selected: new Set(),
  banner: null,
  bannerKey: null,
}

/** True if the user has permanently dismissed a keyed banner. */
export function isDismissed(key) {
  try { return localStorage.getItem('dismissed:' + key) === '1' } catch { return false }
}

export function currentServes(r) { return state.servesOverride[r.id] || r.serves }

export function showList() {
  state.view = 'list'
  state.currentId = null
  render()
}

export function openDetail(id) {
  state.view = 'detail'
  state.currentId = id
  render()
  window.scrollTo(0, 0)
}

function bannerHTML() {
  if (!state.banner) return ''
  return `<div class="banner">
    <div>${state.banner}</div>
    <button data-act="dismissBanner" aria-label="Dismiss">Dismiss</button>
  </div>`
}

export function render() {
  const m = $('main')
  if (state.view === 'detail') {
    const r = byId(state.currentId)
    m.innerHTML = detailHTML(r, r ? currentServes(r) : 0)
  } else {
    m.innerHTML = listHTML(recipes, state, bannerHTML())
  }
  updatePlanCount()
}

/** Re-render the list without losing the caret position in the search box. */
function renderKeepFocus() {
  const el = $('q')
  const pos = el ? el.selectionStart : 0
  render()
  const q = $('q')
  if (q) { q.focus(); try { q.setSelectionRange(pos, pos) } catch {} }
}

export function updatePlanCount() {
  const el = $('plancount')
  if (el) el.textContent = state.selected.size + ' selected'
}

export function setBanner(html, key = null) {
  state.banner = html
  state.bannerKey = key
  if (state.view === 'list') render()
}

/** Called after any write that might have failed on quota. */
async function persist(r) {
  const ok = r ? await saveRecipe(r) : await saveAll()
  if (!ok) {
    setBanner(`<b>That change didn't save.</b> ${lastSaveError?.name === 'QuotaExceededError'
      ? 'This device is out of storage space.'
      : 'Your browser refused the write.'} Export your collection from ⇅ Share data to keep it safe.`)
  }
  return ok
}

/* ================= actions ================= */

registerActions({
  showList,
  closeModal,

  dismissBanner: () => {
    // Some banners (the install hint) should stay dismissed between visits.
    if (state.bannerKey) {
      try { localStorage.setItem('dismissed:' + state.bannerKey, '1') } catch {}
    }
    state.banner = null
    state.bannerKey = null
    render()
  },

  cardClick: d => {
    if (document.body.classList.contains('planmode')) {
      toggleSelected(d.id, !state.selected.has(d.id))
      render()
      return
    }
    openDetail(d.id)
  },

  setCat: d => { state.cat = d.c; render() },

  toggleSel: (d, el) => { toggleSelected(d.id, el.checked); updatePlanCount() },

  togglePlan: () => {
    document.body.classList.toggle('planmode')
    if (!document.body.classList.contains('planmode')) state.selected.clear()
    if (state.view !== 'list') showList(); else render()
  },

  chgServes: d => {
    const r = byId(d.id)
    if (!r) return
    state.servesOverride[d.id] = Math.max(1, currentServes(r) + Number(d.d))
    render()
  },

  resetServes: d => { delete state.servesOverride[d.id]; render() },

  addNote: async d => {
    const el = $('noteIn')
    const v = el?.value.trim()
    if (!v) return
    const r = byId(d.id)
    ;(r.notes = r.notes || []).push({ text: v, date: new Date().toISOString().slice(0, 10) })
    await persist(r)
    render()
  },

  addPairing: async d => {
    const el = $('pairIn')
    const v = el?.value.trim()
    if (!v) return
    const r = byId(d.id)
    ;(r.pairings = r.pairings || []).push({ text: v, date: new Date().toISOString().slice(0, 10) })
    await persist(r)
    render()
  },

  delRecipe: async d => {
    const r = byId(d.id)
    if (!confirm(`Delete "${r ? r.title : 'this recipe'}"? This can't be undone.`)) return
    await removeRecipe(d.id)
    if (state.view === 'detail' && state.currentId === d.id) showList(); else render()
    toast('Recipe deleted')
  },

  /* ---------------- photos ---------------- */

  addHero: d => {
    pickImage(async (blob, aspect) => {
      const r = byId(d.id)
      releaseBlobUrl(r.heroImage)
      r.heroImage = blob
      r.heroAspect = aspect
      if (await persist(r)) toast('Main photo updated')
      render()
    }, 16 / 9)
  },

  editHero: d => {
    const r = byId(d.id)
    if (!r.heroImage) return
    openImageEditor(r.heroImage, r.heroAspect || 16 / 9, async (blob, aspect) => {
      releaseBlobUrl(r.heroImage)
      r.heroImage = blob
      r.heroAspect = aspect
      if (await persist(r)) toast('Photo adjusted')
      render()
    })
  },

  addGallery: d => {
    pickImage(async (blob, aspect) => {
      const r = byId(d.id)
      ;(r.images = r.images || []).push(blob)
      ;(r.imageAspects = r.imageAspects || [])[r.images.length - 1] = aspect
      if (await persist(r)) toast('Photo added')
      render()
    }, 4 / 3)
  },

  editGallery: d => {
    const r = byId(d.id)
    const i = Number(d.i)
    openImageEditor(r.images[i], (r.imageAspects && r.imageAspects[i]) || 4 / 3, async (blob, aspect) => {
      releaseBlobUrl(r.images[i])
      r.images[i] = blob
      ;(r.imageAspects = r.imageAspects || [])[i] = aspect
      if (await persist(r)) toast('Photo adjusted')
      render()
    })
  },

  delGallery: async d => {
    const r = byId(d.id)
    const i = Number(d.i)
    if (!confirm('Remove this photo?')) return
    releaseBlobUrl(r.images[i])
    r.images.splice(i, 1)
    if (r.imageAspects) r.imageAspects.splice(i, 1)
    await persist(r)
    render()
  },
})

registerInputActions({
  search: (d, el) => { state.q = el.value; renderKeepFocus() },
})

function toggleSelected(id, on) {
  if (on) state.selected.add(id); else state.selected.delete(id)
}
